
import ClipperLib from './clipper_unminified.js';
import * as THREE from 'three';
import Voronoi from 'voronoi';
const { nurbs } = require('./nurbs.min.js');
import { polyToPolyLines, convexPolygonCut } from './ConvexPolygonIntersect.js';
import { grid } from './initThree.js';

export let currentMesh;
function vector2ToFPoint(p) {
    return new ClipperLib.FPoint(p.x, p.y);
}

function fPointToVector2(p) {
    return new THREE.Vector2(p.X, p.Y);
}

// 性能优化
function roundRectGetPoints(w, h, r, curveSegments) {
    let points = [];
    // 逆时针，从第一象限的圆弧开始，第四象限的圆弧结束
    let radEach = Math.PI * 0.5 / curveSegments;
    let cx = w - r;
    let cy = h - r;
    points.push(new THREE.Vector2(w, cy));
    for (let i = 1; i < curveSegments; i++) {
        let rad = i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(cx, h));

    cx = -(w - r);
    const PI_2 = Math.PI * 0.5;
    points.push(new THREE.Vector2(cx, h));
    for (let i = 1; i < curveSegments; i++) {
        let rad = PI_2 + i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(-w, cy));

    cy = -(h - r);
    const PI_2_2 = Math.PI;
    points.push(new THREE.Vector2(-w, cy));
    for (let i = 1; i < curveSegments; i++) {
        let rad = PI_2_2 + i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(cx, -h));

    cx = w - r;
    const PI_2_3 = Math.PI * 1.5;
    points.push(new THREE.Vector2(cx, -h));
    for (let i = 1; i < curveSegments; i++) {
        let rad = PI_2_3 + i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(w, cy));

    return points;
}

function isEqual(a, b) {
    return Math.abs(a - b) < 1e-8;
}

class RoundRectCurve {
    constructor(w, h, r, t, curveSegments) {
        this.width = w;
        this.height = h;
        this.radius = r;
        this.innerThickness = t;
        this.curveSegments = curveSegments;
        this.cx = this.width - this.radius;
        this.cy = this.height - this.radius;
        this.arcLen = this.radius * Math.PI * 0.5;
        this.stop1 = this.arcLen;
        this.stop2 = this.stop1 + this.cx * 2;
        this.stop3 = this.stop2 + this.arcLen;
        this.stop4 = this.stop3 + this.cy * 2;
        this.stop5 = this.stop4 + this.arcLen;
        this.stop6 = this.stop5 + this.cx * 2;
        this.stop7 = this.stop6 + this.arcLen;
        // depth = 40; // 高度
    }

    // get depth() { return this.depth; }

    // set depth(value) {
    //     this.depth = value;
    //     this.mesh.position.y = -this.depth * 0.5  + 20;
    //     grid.position.y = this.mesh.position.y;
    // }

    getLength() {
        return (this.arcLen + this.cx + this.cy) * 4;
    }

    getUValues() {
        let uValues = [];
        let arcEach = this.arcLen / this.curveSegments;
        uValues.push(0);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(i * arcEach); }
        uValues.push(this.arcLen);

        let uBegin = this.arcLen + this.cx * 2;
        uValues.push(uBegin);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(uBegin + i * arcEach); }
        uBegin += this.arcLen;
        uValues.push(uBegin);

        uBegin += this.cy * 2;
        uValues.push(uBegin);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(uBegin + i * arcEach); }
        uBegin += this.arcLen;
        uValues.push(uBegin);

        uBegin += this.cx * 2;
        uValues.push(uBegin);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(uBegin + i * arcEach); }
        uBegin += this.arcLen;
        uValues.push(uBegin);

        uBegin += this.cy * 2;
        uValues.push(uBegin)

        return uValues;
    }

    getPointAtOuter(u) {
        let x, y;
        let cx = this.cx, cy = this.cy;
        if (u < this.stop1) {
            let rad = u / this.arcLen * Math.PI * 0.5;
            x = cx + this.radius * Math.cos(rad);
            y = cy + this.radius * Math.sin(rad);
        } else if (u < this.stop2) {
            u -= this.stop1;
            x = cx - u;
            y = this.height;
        } else if (u < this.stop3) {
            u -= this.stop2;
            let rad = Math.PI * 0.5 + u / this.arcLen * Math.PI * 0.5;
            x = -cx + this.radius * Math.cos(rad);
            y = cy + this.radius * Math.sin(rad);
        } else if (u < this.stop4) {
            u -= this.stop3;
            x = -this.width;
            y = cy - u;
        } else if (u < this.stop5) {
            u -= this.stop4;
            let rad = Math.PI + u / this.arcLen * Math.PI * 0.5;
            x = -cx + this.radius * Math.cos(rad);
            y = -cy + this.radius * Math.sin(rad);
        } else if (u < this.stop6) {
            u -= this.stop5;
            x = -cx + u;
            y = -this.height;
        } else if (u < this.stop7) {
            u -= this.stop6;
            let rad = Math.PI * 1.5 + u / this.arcLen * Math.PI * 0.5;
            x = cx + this.radius * Math.cos(rad);
            y = -cy + this.radius * Math.sin(rad);
        } else {
            u -= this.stop7;
            x = this.width;
            y = -cy + u;
        }
        return new THREE.Vector2(x, y);
    }

    getPointAtInner(u, innerThickness) {
        innerThickness = innerThickness || this.innerThickness;
        let x2, y2;
        let cx = this.cx, cy = this.cy;
        if (u < this.stop1) {
            let rad = u / this.arcLen * Math.PI * 0.5;
            x2 = cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = cy + (this.radius - innerThickness) * Math.sin(rad);
        } else if (u < this.stop2) {
            u -= this.stop1;
            x2 = cx - u;
            y2 = this.height - innerThickness;
        } else if (u < this.stop3) {
            u -= this.stop2;
            let rad = Math.PI * 0.5 + u / this.arcLen * Math.PI * 0.5;
            x2 = -cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = cy + (this.radius - innerThickness) * Math.sin(rad);
        } else if (u < this.stop4) {
            u -= this.stop3;
            x2 = -(this.width - innerThickness);
            y2 = cy - u;
        } else if (u < this.stop5) {
            u -= this.stop4;
            let rad = Math.PI + u / this.arcLen * Math.PI * 0.5;
            x2 = -cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = -cy + (this.radius - innerThickness) * Math.sin(rad);
        } else if (u < this.stop6) {
            u -= this.stop5;
            x2 = -cx + u;
            y2 = -(this.height - innerThickness);
        } else if (u < this.stop7) {
            u -= this.stop6;
            let rad = Math.PI * 1.5 + u / this.arcLen * Math.PI * 0.5;
            x2 = cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = -cy + (this.radius - innerThickness) * Math.sin(rad);
        } else {
            u -= this.stop7;
            x2 = this.width - innerThickness;
            y2 = -cy + u;
        }
        return new THREE.Vector2(x2, y2);
    }
}

export class PenholderMaker {
    thickness = 1; // 壁厚
    cutThickness = 0.5; // 切除部分厚度
    width = 40; // 宽度的一半
    height = 40; // 深度的一半
    depth = 120; // 高度
    get borderRadius() { return (Math.min(this.width, this.height) - this.thickness) * this.borderRadiusRatio + this.thickness; } // 圆角半径，不能小于壁厚
    borderRadiusRatio = 0.5;
    // curveSegments = 60; // 圆弧分割段数
    get curveSegments() { return Math.max(20, Math.ceil(this.borderRadius * Math.PI * 0.5 / 0.2)); } // 圆弧分割段数：自动计算
    padding = 1; // 上下边距
    coverTooth = 2; // 盖子卡入凸起高度
    coverHeight = 10; // 盖子自身的高度
    makeHole = true;
    curveCell = true; // 每个格子是多边形，还是曲线

    voronoiEdgeThickness = 1; // 泰森多边形的边的宽度
    voronoiSitesCount = 100; // 随机点的数量
    voronoiReverse = true;

    constructor() { }

    // 等长曲线，外侧
    getPoints() {
        return roundRectGetPoints(this.width, this.height, this.borderRadius, this.curveSegments);
    }

    getPointsAt(shift) {
        return roundRectGetPoints(this.width - shift, this.height - shift, this.borderRadius - shift, this.curveSegments);
    }

    // 等长曲线，内侧
    getPointsInner() {
        return roundRectGetPoints(this.width - this.thickness, this.height - this.thickness, this.borderRadius - this.thickness, this.curveSegments);
    }

    getPointsBetween() {
        return roundRectGetPoints(this.width - this.cutThickness, this.height - this.cutThickness, this.borderRadius - this.cutThickness, this.curveSegments);
    }

    getPRect() {
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments);
        let width = curve.getLength();
        let height = this.depth;
        return { width, height, padding: this.padding };
    }

    makeMesh(scene) {
        let geom = new THREE.BufferGeometry();
        geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(), 3));
        // let material = new THREE.MeshNormalMaterial({
        let material = new THREE.MeshPhongMaterial({
            // let material = new THREE.MeshLambertMaterial({
            // wireframe: true,
            flatShading: true,
        });
        this.mesh = new THREE.Mesh(geom, material);
        this.mesh.rotation.x = -Math.PI * 0.5;
        this.mesh.position.y = -this.depth * 0.5;
        grid.position.y = this.mesh.position.y;
        this.mesh.frustumCulled = false;
        currentMesh = this.mesh;
        scene.add(this.mesh);
    }

    updateGeometry(vertices) {
        this.mesh.geometry.attributes.position = new THREE.BufferAttribute(new Float32Array(vertices), 3);
        this.mesh.position.y = -this.depth * 0.5;
    }

    addCircle(vertices, ps, z) {
        // 底
        for (let i = 0, last = ps[ps.length - 1], curr = ps[0]; i < ps.length; i++, last = curr, curr = ps[i]) {
            vertices.push(0, 0, z, curr.x, curr.y, z, last.x, last.y, z);
        }
    }

    addCircleReverse(vertices, ps, z) {
        // 底
        for (let i = 0, last = ps[ps.length - 1], curr = ps[0]; i < ps.length; i++, last = curr, curr = ps[i]) {
            vertices.push(0, 0, z, last.x, last.y, z, curr.x, curr.y, z);
        }
    }

    // 添加底部：上下两个面
    addBottom(vertices, ps1, ps2) {
        // 底
        for (let i = 0, last = ps1[ps1.length - 1], curr = ps1[0]; i < ps1.length; i++, last = curr, curr = ps1[i]) {
            vertices.push(0, 0, 0, curr.x, curr.y, 0, last.x, last.y, 0);
        }
        // 内部底
        for (let i = 0, last = ps2[ps2.length - 1], curr = ps2[0], z = this.thickness; i < ps2.length; i++, last = curr, curr = ps2[i]) {
            vertices.push(0, 0, z, last.x, last.y, z, curr.x, curr.y, z);
        }
    }

    // 添加底部：上下两个面和内壁
    addBottomAndInnerBound(vertices, ps1, ps2) {
        // 底
        for (let i = 0, last = ps1[ps1.length - 1], curr = ps1[0]; i < ps1.length; i++, last = curr, curr = ps1[i]) {
            vertices.push(0, 0, 0, curr.x, curr.y, 0, last.x, last.y, 0);
        }
        // 内部底
        for (let i = 0, last = ps2[ps2.length - 1], curr = ps2[0], z = this.thickness; i < ps2.length; i++, last = curr, curr = ps2[i]) {
            vertices.push(0, 0, z, last.x, last.y, z, curr.x, curr.y, z);
        }
        // 内壁
        for (let i = 0, last = ps2[ps2.length - 1], z1 = this.thickness, z2 = this.depth; i < ps2.length; i++) {
            let curr = ps2[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, curr.x, curr.y, z1);
            vertices.push(last.x, last.y, z1, last.x, last.y, z2, curr.x, curr.y, z2);
            last = curr;
        }
    }

    // 素的，没有任何图案
    makePlain() {
        // console.time("makePlain");
        let ps1 = this.getPoints();
        let ps2 = this.getPointsInner();
        let vertices = [];
        this.addBottomAndInnerBound(vertices, ps1, ps2);
        // 外壁
        for (let i = 0, last = ps1[ps1.length - 1], z1 = 0, z2 = this.depth; i < ps1.length; i++) {
            let curr = ps1[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z1, curr.x, curr.y, z2);
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, last.x, last.y, z2);
            last = curr;
        }
        // 上边缘
        for (let i = 0, last1 = ps1[ps1.length - 1], last2 = ps2[ps2.length - 1], z = this.depth; i < ps1.length; i++) {
            let curr1 = ps1[i], curr2 = ps2[i];
            vertices.push(last1.x, last1.y, z, curr1.x, curr1.y, z, curr2.x, curr2.y, z);
            vertices.push(last1.x, last1.y, z, curr2.x, curr2.y, z, last2.x, last2.y, z);
            last1 = curr1; last2 = curr2;
        }

        this.updateGeometry(vertices);
        // console.timeEnd("makePlain");
    }

    refreshSites() {
        let sites = [];
        for (let i = 0; i < this.voronoiSitesCount; i++) {
            let x = Math.random();
            let y = Math.random();
            sites.push({ x, y });
        }
        this.sites = sites;
    }

    makeVoronoiPaths() {
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments);
        let curveLength = curve.getLength();
        let voronoi = new Voronoi();
        // 左右：重复2次，上下：砍去padding
        let bbox = { xl: -2 - curveLength, xr: 2 * curveLength + 2, yt: this.padding, yb: this.depth - this.padding };
        let sites = this.sites.flatMap(site => [
            { x: site.x * curveLength, y: site.y * this.depth },
            { x: site.x * curveLength - curveLength, y: site.y * this.depth },
            { x: site.x * curveLength + curveLength, y: site.y * this.depth }
        ]);
        let diagram = voronoi.compute(sites, bbox);
        let paths = [];
        for (let i = 0; i < diagram.cells.length; i++) {
            let cell = diagram.cells[i];
            let halfedges = cell.halfedges;
            if (halfedges.length > 0) {
                let hole = halfedges.map(e => {
                    let p = e.getStartpoint();
                    return new ClipperLib.FPoint(p.x, p.y);
                });
                let solution = new ClipperLib.Paths();
                let co = new ClipperLib.ClipperOffset(2, 0.01);
                co.AddPath(hole, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
                co.Execute(solution, -this.voronoiEdgeThickness * 0.5);

                if (solution.length > 0) {
                    let poly = solution[0].map(fPointToVector2);
                    if (this.curveCell) {
                        let curve = nurbs({
                            points: poly.map(p => [p.x, p.y]),
                            degree: poly.length <= 3 ? 2 : 3,
                            boundary: 'closed'
                        });

                        let pt = [];
                        let points = [];
                        for (let i = 0, n = 50; i <= n; i++) {
                            let d0 = curve.domain[0][0];
                            let d1 = curve.domain[0][1];
                            let t = d0 + (d1 - d0) * i / n;
                            curve.evaluate(pt, t);
                            points.push(new THREE.Vector2(pt[0], pt[1]));
                        }
                        paths.push(points);
                    } else {
                        paths.push(poly);
                    }
                }
            } else {
                console.log(cell);
            }
        }

        // 预处理：将路径转换为上下剖分的两段
        let n = 0, m = 0;
        paths.forEach(path => {
            if (THREE.ShapeUtils.isClockWise(path)) {
                n++;
            } else {
                m++;
            }
        });
        // console.log("clockwise", n, m);
        return paths.map(polyToPolyLines);
    }

    makeModel() {
        this.voronoiPaths = this.makeVoronoiPaths();
        // console.log(this.voronoiPaths);
        if (this.makeHole) {
            this.makeModelWithReliefHole(this.voronoiPaths);
        } else {
            this.makeModelWithRelief(this.voronoiPaths);
        }
    }

    // 带浮雕。带padding。
    makeModelWithRelief(pathsList) {
        // console.time("makeModelWithRelief");
        let ps1 = this.getPoints();
        let ps2 = this.getPointsInner();
        let vertices = [];
        let padding = this.padding + this.voronoiEdgeThickness * 0.5;
        // 底部边缘，内壁
        this.addBottomAndInnerBound(vertices, ps1, ps2);
        // 上边缘
        for (let i = 0, last1 = ps1[ps1.length - 1], last2 = ps2[ps2.length - 1], z = this.depth; i < ps1.length; i++) {
            let curr1 = ps1[i], curr2 = ps2[i];
            vertices.push(last1.x, last1.y, z, curr1.x, curr1.y, z, curr2.x, curr2.y, z);
            vertices.push(last1.x, last1.y, z, curr2.x, curr2.y, z, last2.x, last2.y, z);
            last1 = curr1; last2 = curr2;
        }
        // 上边缘：延伸出一段padding
        for (let i = 0, last = ps1[ps1.length - 1], z1 = this.depth - padding, z2 = this.depth; i < ps1.length; i++) {
            let curr = ps1[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z1, curr.x, curr.y, z2);
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, last.x, last.y, z2);
            last = curr;
        }
        // 下边缘：延伸出一段padding
        for (let i = 0, last = ps1[ps1.length - 1], z1 = 0, z2 = padding; i < ps1.length; i++) {
            let curr = ps1[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z1, curr.x, curr.y, z2);
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, last.x, last.y, z2);
            last = curr;
        }

        // 外壁：分割成一段一段的，再计算
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments); // 曲面
        let uList = curve.getUValues();

        for (let i = 1, uLast = uList[0], uCurr = uList[i], z1 = padding, z2 = this.depth - padding, solution = new ClipperLib.PolyTree(); i < uList.length; i++, uLast = uCurr, uCurr = uList[i]) {
            // 相交
            if (uLast === uCurr) continue;

            // 计算孔洞与矩形的相交部分
            let holes = [];
            for (let path of pathsList) {
                let hole = convexPolygonCut(path, uLast, uCurr);
                if (hole.length > 0) { holes.push(hole); }
            }

            let c = new ClipperLib.Clipper();
            let rect = [[new ClipperLib.FPoint(uLast, z1), new ClipperLib.FPoint(uCurr, z1), new ClipperLib.FPoint(uCurr, z2), new ClipperLib.FPoint(uLast, z2)]];
            c.AddPaths(rect, ClipperLib.PolyType.ptSubject, true);
            c.AddPaths(holes.map(hole => hole.map(vector2ToFPoint)), ClipperLib.PolyType.ptClip, true);
            // 下面将矩形划分成两个部分：A+B。A是非孔洞部分（矩形减去孔洞），B是孔洞部分（矩形交孔洞）。
            // 一般地，A在外层。如果是反相，则A在内层。

            if (!this.voronoiReverse) {
                c.Execute(ClipperLib.ClipType.ctDifference, solution);
            } else {
                c.Execute(ClipperLib.ClipType.ctIntersection, solution);
            }
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = { shape: node.Contour().map(fPointToVector2) };
                path.holes = node.Childs().map(holeNode => holeNode.Contour().map(fPointToVector2));
                const faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
                let points = [...path.shape];
                path.holes.forEach(hole => { points.push(...hole); });
                faces.forEach(f => {
                    let u1 = points[f[0]];
                    let u2 = points[f[1]];
                    let u3 = points[f[2]];
                    let p1 = curve.getPointAtOuter(u1.x);
                    let p2 = curve.getPointAtOuter(u2.x);
                    let p3 = curve.getPointAtOuter(u3.x);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p2.x, p2.y, u2.y);
                    vertices.push(p3.x, p3.y, u3.y);
                });
                node.Childs().forEach(subNode => { nodes.push(...subNode.Childs()); });
            }

            if (!this.voronoiReverse) {
                c.Execute(ClipperLib.ClipType.ctIntersection, solution);
            } else {
                c.Execute(ClipperLib.ClipType.ctDifference, solution);
            }
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = { shape: node.Contour().map(fPointToVector2) };
                let nodeChilds = node.Childs();
                path.holes = nodeChilds.map(holeNode => holeNode.Contour().map(fPointToVector2));
                const faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
                let points = [...path.shape];
                path.holes.forEach(hole => { points.push(...hole); });
                faces.forEach(f => {
                    let u1 = points[f[0]];
                    let u2 = points[f[1]];
                    let u3 = points[f[2]];
                    let p1 = curve.getPointAtInner(u1.x);
                    let p2 = curve.getPointAtInner(u2.x);
                    let p3 = curve.getPointAtInner(u3.x);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p2.x, p2.y, u2.y);
                    vertices.push(p3.x, p3.y, u3.y);
                });
                let bounds = [path.shape, ...path.holes];
                bounds.forEach(hole => {
                    for (let k = 0, last = hole[hole.length - 1], curr = hole[0]; k < hole.length; k++, last = curr, curr = hole[k]) {
                        // 与左右两条线重合的边去除掉
                        if ((curr.x === uLast && last.x === uLast) || (curr.x === uCurr && last.x === uCurr)) continue;
                        // 与上下边缘重合的去除
                        // if ((curr.y === z1 && last.y === z1) || (curr.y === z2 && last.y === z2)) continue;
                        let p1 = curve.getPointAtOuter(last.x);
                        let p2 = curve.getPointAtOuter(curr.x);
                        let p3 = curve.getPointAtInner(last.x);
                        let p4 = curve.getPointAtInner(curr.x);
                        vertices.push(p1.x, p1.y, last.y, p2.x, p2.y, curr.y, p4.x, p4.y, curr.y);
                        vertices.push(p1.x, p1.y, last.y, p4.x, p4.y, curr.y, p3.x, p3.y, last.y);
                    }
                });
                nodeChilds.forEach(subNode => { nodes.push(...subNode.Childs()); });
            }
        }

        this.updateGeometry(vertices);
        // console.timeEnd("makeModelWithRelief");
    }

    // 穿孔，带padding
    makeModelWithReliefHole(pathsList) {
        // console.time("makeModelWithReliefHole");
        let ps1 = this.getPoints();
        let ps2 = this.getPointsInner();
        let vertices = [];
        this.addBottom(vertices, ps1, ps2);
        let padding = this.padding + this.voronoiEdgeThickness * 0.5;
        // 上边缘
        for (let i = 0, last1 = ps1[ps1.length - 1], last2 = ps2[ps2.length - 1], z = this.depth; i < ps1.length; i++) {
            let curr1 = ps1[i], curr2 = ps2[i];
            vertices.push(last1.x, last1.y, z, curr1.x, curr1.y, z, curr2.x, curr2.y, z);
            vertices.push(last1.x, last1.y, z, curr2.x, curr2.y, z, last2.x, last2.y, z);
            last1 = curr1; last2 = curr2;
        }
        // 上边缘：延伸出一段padding
        for (let i = 0, last = ps1[ps1.length - 1], z1 = this.depth - padding, z2 = this.depth; i < ps1.length; i++) {
            let curr = ps1[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z1, curr.x, curr.y, z2);
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, last.x, last.y, z2);
            last = curr;
        }
        // 下边缘：延伸出一段padding
        for (let i = 0, last = ps1[ps1.length - 1], z1 = 0, z2 = padding; i < ps1.length; i++) {
            let curr = ps1[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z1, curr.x, curr.y, z2);
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, last.x, last.y, z2);
            last = curr;
        }
        // 上边缘、内侧：延伸出一段padding
        for (let i = 0, last = ps2[ps2.length - 1], z1 = this.depth - padding, z2 = this.depth; i < ps2.length; i++) {
            let curr = ps2[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, curr.x, curr.y, z1);
            vertices.push(last.x, last.y, z1, last.x, last.y, z2, curr.x, curr.y, z2);
            last = curr;
        }
        // 下边缘、内侧：延伸出一段padding
        for (let i = 0, last = ps2[ps2.length - 1], z1 = this.thickness, z2 = padding; i < ps2.length; i++) {
            let curr = ps2[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, curr.x, curr.y, z1);
            vertices.push(last.x, last.y, z1, last.x, last.y, z2, curr.x, curr.y, z2);
            last = curr;
        }
        // this.updateGeometry(vertices);
        // console.timeEnd("makeModelWithRelief");
        // return;

        // 外壁：分割成一段一段的，再计算
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments); // 曲面
        let uList = curve.getUValues();

        for (let i = 1, uLast = uList[0], uCurr = uList[i], z1 = padding, z2 = this.depth - padding, solution = new ClipperLib.PolyTree(); i < uList.length; i++, uLast = uCurr, uCurr = uList[i]) {
            // 相交
            if (uLast === uCurr) continue;

            // 计算相交部分
            let holes = [];
            for (let path of pathsList) {
                let hole = convexPolygonCut(path, uLast, uCurr);
                if (hole.length > 0) { holes.push(hole); }
            }

            let c = new ClipperLib.Clipper();
            let rect = [[new ClipperLib.FPoint(uLast, z1), new ClipperLib.FPoint(uCurr, z1), new ClipperLib.FPoint(uCurr, z2), new ClipperLib.FPoint(uLast, z2)]];
            c.AddPaths(rect, ClipperLib.PolyType.ptSubject, true);
            c.AddPaths(holes.map(hole => hole.map(vector2ToFPoint)), ClipperLib.PolyType.ptClip, true);
            c.Execute(ClipperLib.ClipType.ctDifference, solution);
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = {
                    shape: node.Contour().map(fPointToVector2),
                    holes: node.Childs().map(holeNode => holeNode.Contour().map(fPointToVector2))
                };
                const faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
                let points = [path.shape, path.holes].flat(2);
                faces.forEach(f => {
                    let u1 = points[f[0]];
                    let u2 = points[f[1]];
                    let u3 = points[f[2]];
                    let p1 = curve.getPointAtOuter(u1.x);
                    let p2 = curve.getPointAtOuter(u2.x);
                    let p3 = curve.getPointAtOuter(u3.x);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p2.x, p2.y, u2.y);
                    vertices.push(p3.x, p3.y, u3.y);
                    p1 = curve.getPointAtInner(u1.x, this.thickness);
                    p2 = curve.getPointAtInner(u2.x, this.thickness);
                    p3 = curve.getPointAtInner(u3.x, this.thickness);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p3.x, p3.y, u3.y);
                    vertices.push(p2.x, p2.y, u2.y);
                });
                [path.shape, ...path.holes].forEach(points => {
                    for (let k = 0, last = points[points.length - 1], curr = points[0]; k < points.length; k++, last = curr, curr = points[k]) {
                        if ((curr.x === uLast && last.x === uLast) || (curr.x === uCurr && last.x === uCurr)) continue;
                        if ((curr.y === z1 && last.y === z1) || (curr.y === z2 && last.y === z2)) continue;
                        let p1 = curve.getPointAtOuter(last.x);
                        let p2 = curve.getPointAtOuter(curr.x);
                        let p3 = curve.getPointAtInner(last.x, this.thickness);
                        let p4 = curve.getPointAtInner(curr.x, this.thickness);
                        vertices.push(p1.x, p1.y, last.y, p4.x, p4.y, curr.y, p2.x, p2.y, curr.y);
                        vertices.push(p1.x, p1.y, last.y, p3.x, p3.y, last.y, p4.x, p4.y, curr.y);
                    }
                });
                node.Childs().forEach(subNode => { nodes.push(...subNode.Childs()); });
            };

            c.Execute(ClipperLib.ClipType.ctIntersection, solution);
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = { shape: node.Contour().map(fPointToVector2) };
                let nodeChilds = node.Childs();
                path.holes = nodeChilds.map(holeNode => holeNode.Contour().map(fPointToVector2));
                let bounds = [path.shape, ...path.holes];
                bounds.forEach(hole => {
                    for (let k = 0, last = hole[hole.length - 1], curr = hole[0]; k < hole.length; k++, last = curr, curr = hole[k]) {
                        // 与上下边缘重合的加上
                        if ((curr.y === z1 && last.y === z1) || (curr.y === z2 && last.y === z2)) {
                            let p1 = curve.getPointAtOuter(last.x, this.thickness);
                            let p2 = curve.getPointAtOuter(curr.x, this.thickness);
                            let p3 = curve.getPointAtInner(last.x, this.thickness);
                            let p4 = curve.getPointAtInner(curr.x, this.thickness);
                            vertices.push(p1.x, p1.y, last.y, p2.x, p2.y, curr.y, p4.x, p4.y, curr.y);
                            vertices.push(p1.x, p1.y, last.y, p4.x, p4.y, curr.y, p3.x, p3.y, last.y);
                        }
                    }
                });
                nodeChilds.forEach(subNode => { nodes.push(...subNode.Childs()); });
            }
        }

        this.updateGeometry(vertices);
        // console.timeEnd("makeModelWithReliefHole");
    }

    addBounds(vertices, ps, z1, z2) {
        // 内壁
        for (let i = 0, last = ps[ps.length - 1]; i < ps.length; i++) {
            let curr = ps[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, curr.x, curr.y, z1);
            vertices.push(last.x, last.y, z1, last.x, last.y, z2, curr.x, curr.y, z2);
            last = curr;
        }
    }

    addBands(vertices, ps1, ps2, z) {
        for (let i = 0, last1 = ps1[ps1.length - 1], last2 = ps2[ps2.length - 1]; i < ps1.length; i++) {
            let curr1 = ps1[i], curr2 = ps2[i];
            vertices.push(last1.x, last1.y, z, curr1.x, curr1.y, z, curr2.x, curr2.y, z);
            vertices.push(last1.x, last1.y, z, curr2.x, curr2.y, z, last2.x, last2.y, z);
            last1 = curr1; last2 = curr2;
        }
    }

}



// WEBPACK FOOTER //
// ./src/views/makeTysonPenHolder/scripts/PenholderMaker.js